import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Edit2, Check, X, Users } from "lucide-react";
import customFieldsService from "../services/customFieldsService";
import segmentsService from "../services/segmentsService";
import { CustomFieldDefinition, CustomFieldType } from "../types/customFields";
import { Segment } from "../types/segment";

const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: "Text",
  number: "Number",
  select: "Select",
  date: "Date",
  boolean: "Boolean",
};

const CustomFieldDefinitions: React.FC = () => {
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<CustomFieldType>("text");
  const [newChoices, setNewChoices] = useState("");
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editChoices, setEditChoices] = useState("");

  // Bulk assign modal
  const [bulkField, setBulkField] = useState<CustomFieldDefinition | null>(null);
  const [bulkValue, setBulkValue] = useState<string>("");
  const [bulkScope, setBulkScope] = useState<"all" | "segment">("all");
  const [bulkSegmentId, setBulkSegmentId] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);

  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = async () => {
    try {
      const data = await customFieldsService.listDefinitions();
      setDefinitions(data);
    } catch (err) {
      console.error("Failed to load field definitions:", err);
      setError("Failed to load fields");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;

    setCreating(true);
    try {
      const options: { choices?: string[] } = {};
      if (newType === "select") {
        const choices = newChoices.split(",").map((c) => c.trim()).filter(Boolean);
        if (choices.length === 0) {
          setError("Select fields must have at least one choice");
          setCreating(false);
          return;
        }
        options.choices = choices;
      }

      await customFieldsService.createDefinition(newName.trim(), newType, options);
      setNewName("");
      setNewType("text");
      setNewChoices("");
      setShowCreateForm(false);
      await loadDefinitions();
    } catch (err: any) {
      setError(err.response?.data?.messages?.join(", ") || "Failed to create field");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;

    try {
      const def = definitions.find((d) => d.id === id);
      const data: any = { name: editName.trim() };
      if (def?.field_type === "select") {
        const choices = editChoices.split(",").map((c) => c.trim()).filter(Boolean);
        if (choices.length === 0) {
          setError("Select fields must have at least one choice");
          return;
        }
        data.options = { choices };
      }
      await customFieldsService.updateDefinition(id, data);
      setEditingId(null);
      await loadDefinitions();
    } catch (err: any) {
      setError(err.response?.data?.messages?.join(", ") || "Failed to update field");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this field? All subscriber values for this field will be removed.")) return;

    try {
      await customFieldsService.deleteDefinition(id);
      await loadDefinitions();
    } catch (err) {
      setError("Failed to delete field");
    }
  };

  const openBulkAssign = async (def: CustomFieldDefinition) => {
    setBulkField(def);
    setBulkValue("");
    setBulkScope("all");
    setBulkSegmentId("");
    try {
      const segs = await segmentsService.listSegments();
      setSegments(segs);
    } catch (err) {
      console.error("Failed to load segments:", err);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkField) return;
    if (bulkScope === "segment" && !bulkSegmentId) {
      setError("Please select a segment");
      return;
    }

    let parsedValue: string | number | boolean = bulkValue;
    if (bulkField.field_type === "number") {
      parsedValue = parseFloat(bulkValue);
      if (isNaN(parsedValue)) {
        setError("Please enter a valid number");
        return;
      }
    } else if (bulkField.field_type === "boolean") {
      parsedValue = bulkValue === "true";
    }

    setBulkSubmitting(true);
    try {
      await customFieldsService.bulkAssign(
        bulkField.id,
        parsedValue,
        bulkScope,
        bulkScope === "segment" ? bulkSegmentId : undefined
      );
      setBulkField(null);
      setError("");
      alert("Bulk assignment started. Values will be updated shortly.");
    } catch (err: any) {
      setError(err.response?.data?.error || "Bulk assignment failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const startEdit = (def: CustomFieldDefinition) => {
    setEditingId(def.id);
    setEditName(def.name);
    setEditChoices(def.field_type === "select" ? (def.options?.choices || []).join(", ") : "");
  };

  if (loading) return <div style={{ padding: "20px" }}>Loading...</div>;

  return (
    <div style={{ padding: "20px", maxWidth: "800px" }}>
      <div style={{ marginBottom: "20px" }}>
        <button
          onClick={() => navigate("/subscribers")}
          style={{
            padding: "4px 0",
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#6b7280",
            fontSize: "14px",
          }}
        >
          &larr; Back to Subscribers
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ margin: 0 }}>Custom Fields</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            backgroundColor: "#000",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          <Plus size={16} /> Add Field
        </button>
      </div>

      {error && (
        <div style={{ color: "red", marginBottom: "16px", fontSize: "14px" }}>
          {error}
          <button onClick={() => setError("")} style={{ marginLeft: "8px", border: "none", background: "none", cursor: "pointer", color: "red" }}>
            dismiss
          </button>
        </div>
      )}

      {showCreateForm && (
        <div
          style={{
            marginBottom: "24px",
            padding: "16px",
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          }}
        >
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px" }}>New Field</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                placeholder="Field name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as CustomFieldType)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {newType === "select" && (
              <input
                type="text"
                placeholder="Choices (comma-separated, e.g. Option A, Option B)"
                value={newChoices}
                onChange={(e) => setNewChoices(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            )}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: creating ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: creating || !newName.trim() ? 0.5 : 1,
                }}
              >
                {creating ? "Creating..." : "Create"}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setNewName(""); setNewType("text"); setNewChoices(""); }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#fff",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {definitions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
          <p>No custom fields defined yet.</p>
          <p style={{ fontSize: "14px" }}>Custom fields let you add metadata to subscribers (e.g., Company, Plan Tier, City).</p>
        </div>
      ) : (
        <div style={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Name</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Type</th>
                <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Options</th>
                <th style={{ padding: "10px 16px", textAlign: "right", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {definitions.map((def) => (
                <tr key={def.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "10px 16px", fontSize: "14px" }}>
                    {editingId === def.id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px" }}
                      />
                    ) : (
                      def.name
                    )}
                  </td>
                  <td style={{ padding: "10px 16px", fontSize: "14px", color: "#6b7280" }}>
                    {FIELD_TYPE_LABELS[def.field_type]}
                  </td>
                  <td style={{ padding: "10px 16px", fontSize: "14px", color: "#6b7280" }}>
                    {editingId === def.id && def.field_type === "select" ? (
                      <input
                        type="text"
                        value={editChoices}
                        onChange={(e) => setEditChoices(e.target.value)}
                        placeholder="Comma-separated choices"
                        style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px", width: "200px" }}
                      />
                    ) : def.field_type === "select" ? (
                      (def.options?.choices || []).join(", ")
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={{ padding: "10px 16px", textAlign: "right" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "4px" }}>
                      {editingId === def.id ? (
                        <>
                          <button onClick={() => handleUpdate(def.id)} title="Save" style={{ padding: "4px", border: "none", background: "none", cursor: "pointer", color: "#10b981" }}>
                            <Check size={16} />
                          </button>
                          <button onClick={() => setEditingId(null)} title="Cancel" style={{ padding: "4px", border: "none", background: "none", cursor: "pointer", color: "#6b7280" }}>
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => openBulkAssign(def)} title="Set value for subscribers" style={{ padding: "4px", border: "none", background: "none", cursor: "pointer", color: "#6b7280" }}>
                            <Users size={16} />
                          </button>
                          <button onClick={() => startEdit(def)} title="Edit" style={{ padding: "4px", border: "none", background: "none", cursor: "pointer", color: "#6b7280" }}>
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDelete(def.id)} title="Delete" style={{ padding: "4px", border: "none", background: "none", cursor: "pointer", color: "#dc2626" }}>
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {bulkField && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setBulkField(null)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "420px",
              maxWidth: "90vw",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0" }}>Set "{bulkField.name}" Value</h3>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "4px", color: "#374151" }}>
                Scope
              </label>
              <select
                value={bulkScope}
                onChange={(e) => setBulkScope(e.target.value as "all" | "segment")}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px" }}
              >
                <option value="all">All subscribers</option>
                <option value="segment">Specific segment</option>
              </select>
            </div>

            {bulkScope === "segment" && (
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "4px", color: "#374151" }}>
                  Segment
                </label>
                <select
                  value={bulkSegmentId}
                  onChange={(e) => setBulkSegmentId(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px" }}
                >
                  <option value="">Select a segment...</option>
                  {segments.map((seg) => (
                    <option key={seg.id} value={seg.id}>
                      {seg.name} ({seg.subscriber_count?.toLocaleString() ?? "?"} subscribers)
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "4px", color: "#374151" }}>
                Value
              </label>
              {bulkField.field_type === "select" ? (
                <select
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px" }}
                >
                  <option value="">Select a value...</option>
                  {(bulkField.options?.choices || []).map((choice) => (
                    <option key={choice} value={choice}>{choice}</option>
                  ))}
                </select>
              ) : bulkField.field_type === "boolean" ? (
                <select
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px" }}
                >
                  <option value="">Select...</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : bulkField.field_type === "date" ? (
                <input
                  type="date"
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px", boxSizing: "border-box" }}
                />
              ) : (
                <input
                  type={bulkField.field_type === "number" ? "number" : "text"}
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder={`Enter ${bulkField.field_type} value`}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "14px", boxSizing: "border-box" }}
                />
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setBulkField(null)}
                style={{ padding: "8px 16px", backgroundColor: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: "4px", cursor: "pointer", fontSize: "14px" }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={bulkSubmitting || !bulkValue}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: bulkSubmitting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: bulkSubmitting || !bulkValue ? 0.5 : 1,
                }}
              >
                {bulkSubmitting ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldDefinitions;
